import { Popover, Table, Tag } from 'antd'
import { useStatsAllProjects } from '../../../../hooks/useStatsAllProjects'
import { ContractGoal } from '../../../../Types'
import { CheckCircleOutlined, ClockCircleOutlined } from '@ant-design/icons'
import { STATS_PAGE } from '../../../../constants'
import { Link } from 'react-router-dom'
const { Column, ColumnGroup } = Table
const siteColumnStyle = {
  borderLeft: '1px solid #adadad', // Thicker left border
} as const

interface CostPer {
  click: number
  signup: number
  qLead: number
  rando: number
  predictedRandomization: number
  plannedRandomization: number
  tooltip: string
}

interface FinancialPlanning {
  budget: number
  spend: number
  totalSpendTooltip: string
  budgetRemaining: number
  currentSpend: number // not used!
  /** How many more signups should we find for our advertising? assuming the forecastedEnrollments is true */
  neededSignups: number
  /** How many more screenings should we find for our advertising? assuming the forecastedEnrollments is true */
  neededScreenings: number
  /** explains the calculation. */
  neededSignupsTooltip: string
  dailySpendLasts: number // days
  /** dkk normally. */
  dailySpend: number
  dailySpendTooltip: string
  activeCampaigns: string[]
  numCampaigns: number
  campaignsTooltip: string
  isGoalReached: boolean
  isGoalForecastedReached: boolean
}

interface ScreenFailure {
  expected: number
  actual: number
  tooltip: string
}

export interface ProjectsTableData {
  key: string

  color?: string
  project: string
  projectId: string
  activeSites: string[]
  inactiveSites: string[]
  sitesTooltip: string
  costPer: CostPer
  fp: FinancialPlanning
  screenFailure: ScreenFailure
  newestGoal: ContractGoal
}

export function ProjectsTable({ visibilityPerProject }: { visibilityPerProject: boolean[] }) {
  const { statsPerProject } = useStatsAllProjects()

  console.log('statsperproject:', statsPerProject) // TODOOO HERRR

  if (!statsPerProject) return null

  const data: ProjectsTableData[] = statsPerProject
    .filter((project, idx) => visibilityPerProject[idx])
    .map((project) => {
      return {
        ...project.tableData,
        color: project.color,
      }
    })

  return (
    <Table
      className="projectTable"
      bordered
      dataSource={data.map((row) => flattenObject(row))}
      size="small"
      pagination={false}
    >
      <Column
        title="Project"
        render={(text, _, index) => {
          const record = data[index]
          return (
            <Link className="text-decoration-none" to={STATS_PAGE.gotoPage(record.projectId)}>
              <p style={{ color: record.color, fontWeight: 'bold' }}>
                {text}
                <span className="text-muted" style={{ fontSize: 10 }}>
                  <br />
                  {record.fp.numCampaigns > 0 ? ' ' : <span className="text-danger">No campaigns attached!</span>}{' '}
                  &nbsp;
                </span>
              </p>
            </Link>
          )
        }}
        dataIndex="project"
        key="project"
        width={180}
      />
      <Column
        title="Sites"
        render={(text, _, index) => {
          const record = data[index]
          return (
            <CellTooltip content={record.sitesTooltip}>
              <p>
                {record.activeSites.length}
                <span className="text-muted" style={{ fontSize: 10 }}>
                  <br />
                  Incl. inactive: {record.activeSites.length + record.inactiveSites.length}
                </span>
              </p>
            </CellTooltip>
          )
        }}
        dataIndex="activeSites"
        key="activeSites"
        width={100}
        onHeaderCell={() => ({ style: { ...siteColumnStyle } })}
        onCell={() => ({ style: siteColumnStyle })}
      />
      <Column
        width={120}
        render={(text, _, index) => {
          const record = data[index]
          return (
            <CellTooltip content={record.screenFailure.tooltip}>
              <p>
                {Math.round(record.screenFailure.actual * 100)}%
                <span className="text-muted" style={{ fontSize: 10 }}>
                  <br />
                  Expected: {record.screenFailure.expected * 100}%
                </span>
              </p>
            </CellTooltip>
          )
        }}
        title="Screen Failure"
        dataIndex="screenFailure.actual"
        key="screenFailure.actual"
        onHeaderCell={() => ({ style: siteColumnStyle })}
        onCell={() => ({ style: siteColumnStyle })}
      />
      <ColumnGroup
        onHeaderCell={() => ({ style: siteColumnStyle })}
        title={
          <p>
            Cost per
            <span className="text-muted">&nbsp;(dkk)</span>
          </p>
        }
      >
        <Column
          title="Click"
          onHeaderCell={() => ({ style: siteColumnStyle })}
          onCell={() => ({ style: siteColumnStyle })}
          render={(text, _, index) => {
            const record = data[index]

            return (
              <CellTooltip content={record.costPer.tooltip}>
                <ColValue value={text} />
              </CellTooltip>
            )
          }}
          dataIndex="costPer.click"
          key="costPer.click"
        />
        <Column
          title="Signup"
          render={(text, _, index) => {
            const record = data[index]

            return (
              <CellTooltip content={record.costPer.tooltip}>
                <ColValue value={text} />
              </CellTooltip>
            )
          }}
          dataIndex="costPer.signup"
          key="costPer.signup"
        />
        <Column
          title="Q-Lead"
          render={(text, _, index) => {
            const record = data[index]

            return (
              <CellTooltip content={record.costPer.tooltip}>
                <ColValue value={text} />
              </CellTooltip>
            )
          }}
          dataIndex="costPer.qLead"
          key="costPer.qLead"
        />
        <Column
          title="Rando"
          render={(text, _, index) => {
            const record = data[index]

            if (!text) return null

            const hasPlannedRandoCost = record.costPer?.plannedRandomization && record.costPer?.plannedRandomization > 0

            const needsAttention = hasPlannedRandoCost
              ? Number(record.costPer.predictedRandomization ?? 0) > Number(record.costPer.plannedRandomization ?? 0)
              : false

            return (
              <CellTooltip content={record.costPer.tooltip}>
                <div>
                  <p>{toLocale(text)}</p>
                  <p style={{ lineHeight: '10px' }}>
                    <span className="text-muted" style={{ fontSize: 10 }}>
                      <span style={{ color: needsAttention ? 'red' : 'green' }}>
                        Forecast: {toLocale(record.costPer.predictedRandomization)}
                      </span>
                      {hasPlannedRandoCost ? (
                        <span>
                          <br />
                          Planned: {toLocale(record.costPer.plannedRandomization)}
                        </span>
                      ) : null}
                    </span>
                  </p>
                </div>
              </CellTooltip>
            )
          }}
          dataIndex="costPer.rando"
          key="costPer.rando"
        />
      </ColumnGroup>
      <ColumnGroup title={<p>Financial Planning</p>} onHeaderCell={() => ({ style: siteColumnStyle })}>
        <Column
          title="Campaigns"
          dataIndex="fp.activeCampaigns"
          key="fp.activeCampaigns"
          onHeaderCell={() => ({ style: siteColumnStyle })}
          onCell={() => ({ style: siteColumnStyle })}
          render={(text, _, index) => {
            const record = data[index]

            const totalCampaigns = record.fp.numCampaigns
            if (!totalCampaigns) return null

            return (
              <CellTooltip content={record.fp.campaignsTooltip}>
                <p>
                  {record.fp.activeCampaigns.length}
                  <span className="text-muted" style={{ fontSize: 10 }}>
                    <br />
                    Incl. inactive: {totalCampaigns}
                  </span>
                </p>
              </CellTooltip>
            )
          }}
        />
        <Column
          title={
            <p>
              Daily Spend<span className="text-muted">&nbsp;(dkk)</span>
            </p>
          }
          render={(text, _, index) => {
            const record = data[index]
            if (!text) return null

            return (
              <CellTooltip content={record.fp.dailySpendTooltip}>
                <p>
                  {toLocale(text)}
                  <span className="text-muted" style={{ fontSize: 10 }}>
                    <br />
                    Lasts: {record.fp.dailySpendLasts} days
                  </span>
                </p>
              </CellTooltip>
            )
          }}
          dataIndex="fp.dailySpend"
          key="fp.dailySpend"
        />
        <Column
          title={
            <p>
              Total Spend<span className="text-muted">&nbsp;(dkk)</span>
            </p>
          }
          render={(text, _, index) => {
            const record = data[index]
            if (!record.fp.spend && !record.fp.budget) return null

            const style = record.fp.budgetRemaining < 0 ? { color: 'red' } : {}

            return (
              <CellTooltip content={record.fp.totalSpendTooltip}>
                <p>
                  {Math.round(record.fp.spend / 1000)}k / {Math.round(record.fp.budget / 1000)}k
                  <span className="text-muted" style={{ fontSize: 10 }}>
                    <br /> <span style={style}>Remaining: {Math.round(record.fp.budgetRemaining / 1000)}k</span>
                  </span>
                </p>
              </CellTooltip>
            )
          }}
          dataIndex="fp.budget"
          key="fp.budget"
        />

        <Column
          title="Status"
          render={(text, _, index) => {
            const record = data[index]

            return (
              <CellTooltip content={record.fp.neededSignupsTooltip}>
                {record.fp.isGoalReached ? (
                  <Tag icon={<CheckCircleOutlined />} color="success">
                    Goal reached
                  </Tag>
                ) : record.fp.isGoalForecastedReached ? (
                  <Tag icon={<ClockCircleOutlined />} color="processing">
                    Forecasted goal-reach
                  </Tag>
                ) : (
                  <>
                    <Tag color="default">{toLocale(text)} signups needed</Tag>
                    <p>
                      <span className="text-muted" style={{ fontSize: 10 }}>
                        {`(${toLocale(record.fp.neededScreenings)} screenings)`}
                      </span>
                    </p>
                  </>
                )}
              </CellTooltip>
            )
          }}
          dataIndex="fp.neededSignups"
          key="fp.neededSignups"
        />
      </ColumnGroup>
    </Table>
  )
}

function flattenObject(obj: any, parentKey: string = '', result: any = {}): any {
  for (const key in obj) {
    if (obj.hasOwnProperty(key)) {
      const newKey = parentKey ? `${parentKey}.${key}` : key
      if (typeof obj[key] === 'object' && obj[key] !== null) {
        flattenObject(obj[key], newKey, result)
      } else {
        result[newKey] = obj[key]
      }
    }
  }

  return result
}

/** we want the same spacing and line height as for the custom. */
/* function DefaultRender(value, _1, _2) {
  return <ColValue value={value} />
} */

function ColValue({
  value,
  desc,
  style,
}: {
  value: string | number
  desc?: string | JSX.Element
  style?: React.CSSProperties | undefined
}) {
  if (!value) return null

  return (
    <p style={style ?? {}}>
      {toLocale(value)}
      <span className="text-muted" style={{ fontSize: 10 }}>
        <br />
        {desc} &nbsp;
      </span>
    </p>
  )
}

function toLocale(value: any) {
  return typeof value === 'number' ? value.toLocaleString() : value
}

function CellTooltip({ content, children }: { content: string; children: any }) {
  const contentJsx = (
    <div>
      {content.split('\n').map((text, idx) => (
        <p key={`celltooltip${idx}`}>{text}&nbsp;</p>
      ))}
    </div>
  )

  return (
    <div style={{ position: 'relative' }}>
      <Popover content={contentJsx} title={<b>Details</b>}>
        <div style={{ flex: 1 }}>{children}</div>
      </Popover>
    </div>
  )
}
